import * as fs from 'firebase/firestore';

let readSeq = 0;

/**
 * Firestore read logging on local dev:
 * - Default ON when hostname is localhost / 127.0.0.1
 * - Force ON: VITE_DEBUG_FIRESTORE=true
 * - Force OFF: VITE_DEBUG_FIRESTORE=false
 *
 * Logs document paths and best-effort query summaries so you can align rules/indexes and docs like TENANT_QUERY_INFORMATION.md.
 */
export function isFirestoreReadDebugEnabled() {
    if (typeof window === 'undefined') return false;
    if (import.meta.env.VITE_DEBUG_FIRESTORE === 'false') return false;
    if (import.meta.env.VITE_DEBUG_FIRESTORE === 'true') return true;
    const h = window.location?.hostname || '';
    return h === 'localhost' || h === '127.0.0.1';
}

function summarizeReadTarget(refOrQuery) {
    if (!refOrQuery) return null;
    if (typeof refOrQuery.path === 'string') {
        return { kind: 'document', path: refOrQuery.path };
    }
    try {
        const iq = refOrQuery._query ?? refOrQuery._delegate?._query;
        if (iq) {
            const segs = iq.path?.segments;
            const path = Array.isArray(segs) ? segs.join('/') : String(iq.path ?? '');
            return {
                kind: 'query',
                path: path || '(collectionGroup or unknown)',
                limit: iq.limitType ?? iq.limit ?? undefined,
            };
        }
    } catch {
        // ignore
    }
    return { kind: 'query', note: 'expand raw in console', raw: refOrQuery };
}

function logFirestoreRead(op, firstArg) {
    if (!isFirestoreReadDebugEnabled()) return;
    const id = ++readSeq;
    const summary = summarizeReadTarget(firstArg);
    console.warn(`[Firestore #${id} ${op}]`, summary);
}

function logFirestoreError(op, err) {
    if (!isFirestoreReadDebugEnabled()) return;
    console.error(`[Firestore ${op}]`, err);
    const msg = err?.message || '';
    if (msg.includes('index') || msg.includes('Index')) {
        console.warn(
            '[Firestore] This error often includes a console URL to create a composite index. Open that link to add the index in the Firebase console.'
        );
    }
}

export async function getDoc(reference) {
    logFirestoreRead('getDoc', reference);
    try {
        return await fs.getDoc(reference);
    } catch (e) {
        logFirestoreError('getDoc', e);
        throw e;
    }
}

export async function getDocs(queryArg) {
    logFirestoreRead('getDocs', queryArg);
    try {
        return await fs.getDocs(queryArg);
    } catch (e) {
        logFirestoreError('getDocs', e);
        throw e;
    }
}

export async function getCountFromServer(queryArg) {
    logFirestoreRead('getCountFromServer', queryArg);
    try {
        return await fs.getCountFromServer(queryArg);
    } catch (e) {
        logFirestoreError('getCountFromServer', e);
        throw e;
    }
}

export function onSnapshot(...args) {
    logFirestoreRead('onSnapshot (listen)', args[0]);
    return fs.onSnapshot(...args);
}

export {
    addDoc,
    arrayUnion,
    collectionGroup,
    collection,
    deleteDoc,
    doc,
    getFirestore,
    increment,
    limit,
    orderBy,
    query,
    runTransaction,
    serverTimestamp,
    setDoc,
    startAfter,
    updateDoc,
    where,
    writeBatch,
} from 'firebase/firestore';
